import { useEffect, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Icon } from '../../components/ui/Icon.js';
import { formatDuration } from '../../lib/format.js';
import { usePhone } from './PhoneProvider.js';
import { readable } from './AgentWorkspace.js';

/**
 * The call, in the header of every page but the workspace (3 October 2026): the phone is the
 * portal's now, so somebody who opened their call history mid-call still sees who they are
 * talking to, can hang up, and can go back -- and a call ringing while they are elsewhere is
 * answered from here. The tab's title says so too, for a window in the background.
 */
export function CallBar() {
  const { phone } = usePhone();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const call = phone?.call ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [call]);

  const ringing = call?.state === 'ringing' && call.inbound;
  useEffect(() => {
    if (!ringing) return;
    const was = document.title;
    let on = false;
    const t = setInterval(() => { on = !on; document.title = on ? `Incoming call · ${call!.name ?? call!.number}` : was; }, 1000);
    return () => { clearInterval(t); document.title = was; };
  }, [ringing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!phone || !call || path === '/agent') return null;
  const who = call.name ?? readable(call.number);
  const status = ringing ? 'Incoming call' : call.state === 'calling' ? 'Calling'
    : call.state === 'held' ? 'On hold' : call.answeredAt ? formatDuration(Math.floor((now - call.answeredAt) / 1000)) : '';
  return (
    <div className="arc-callbar" data-ringing={ringing || undefined} role="status">
      <Icon name={call.inbound ? 'callIn' : 'callOut'} size={14} />
      <strong className="arc-truncate">{who}</strong>
      <span className="arc-num arc-callbar-time">{status}</span>
      {ringing
        ? <button type="button" className="arc-btn arc-btn-sm arc-callbar-answer" onClick={phone.answer}>Answer</button>
        : <Link to="/agent" className="arc-btn arc-btn-sm">Open</Link>}
      <button type="button" className="arc-btn arc-btn-sm arc-callbar-end" onClick={phone.hangUp}>
        {ringing ? 'Refuse' : 'Hang up'}
      </button>
    </div>
  );
}

/**
 * Click-to-dial (owner, 3 October 2026): a small call button beside a number anywhere in the
 * portal, shown only when this person's workspace phone is logged in and free.
 */
export function DialButton({ number, label }: { number: string | null | undefined; label?: string | undefined }) {
  const { phone, shift } = usePhone();
  const n = (number ?? '').trim();
  if (!phone || !shift?.shiftId || phone.state !== 'ready' || phone.call || shift.state === 'wrap_up' || !/^\+?[0-9*#]{2,20}$/.test(n)) return null;
  return (
    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm arc-dial-btn"
      title={`Call ${label ?? n}`} aria-label={`Call ${label ?? n}`}
      onClick={(e) => { e.stopPropagation(); phone.dial(n); }}>
      <Icon name="phone" size={13} />
    </button>
  );
}
